import React, { memo, useEffect } from 'react';
import styled from 'styled-components/macro';
import prev from './asset/prev.svg';
import info from './asset/info.svg';
import plus from './asset/plus.svg';
import Switch from 'react-switch';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import UpArrow from './asset/UpArrow.svg';
import Select from 'react-select';
import { customStyles } from 'constants/constants';
import { useFormik } from 'formik';
import { useAssetAllocatorSlice } from './slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssets, selectGoals } from './slice/selectors';
import { formatCurrency } from 'utils/currency';
import { Asset, GoalData } from './slice/types';

interface Props {}

export const AssetAllocator = memo((props: Props) => {
  // const { t, i18n } = useTranslation();
  const [checked, setChecked] = React.useState(false);
  const [AssetType, setAssetType] = React.useState('equity');
  const [initial_amount, setInitial_amount] = React.useState(0);
  const [goalPercentage, setGoalpercentage] = React.useState('0');
  // const [selectedGoal, setSelectedGoal] = React.useState(null);
  const [selectedGoal, setSelectedGoal] = React.useState<GoalData | null>(null);
  const [showAssetTable, setShowAssetTable] = React.useState(false);
  // const [allAsset, setAllAsset] = React.useState<any>([]);

  const formik = useFormik({
    initialValues: {
      goal: '',
      contributor: '',
      asset: '',
      sliderValues: {},
    },
    onSubmit: values => {
      console.log('Form data submitted:', values);
    },
  });
  function calculatePercentageOf(first: number, second: number): string {
    if (
      typeof first !== 'number' ||
      typeof second !== 'number' ||
      isNaN(first) ||
      isNaN(second) ||
      !isFinite(first) ||
      !isFinite(second)
    ) {
      return '0';
    }

    if (first === 0) {
      return '0';
    }

    if (first < 0 || second < 0) {
      return '0';
    }

    const percentage = (second / first) * 100;
    return percentage.toFixed(0);
  }

  const handleSliderChange = (assetName: string, value: number) => {
    const prevValue = formik.values.sliderValues[assetName] || 0;
    const difference = value - prevValue;
    setInitial_amount(prevAmount => prevAmount + difference);
    setGoalpercentage(calculatePercentageOf(10000, initial_amount));
    formik.setFieldValue('sliderValues', {
      ...formik.values.sliderValues,
      [assetName]: value,
    });
  };

  const handleChange = () => {
    setChecked(!checked);
    if (!checked) {
      setShowAssetTable(true);
    } else {
      setShowAssetTable(false);
    }
  };

  const handleAssetChange = selectedAsset => {
    if (selectedAsset && selectedAsset.value) {
      const selectedValue = selectedAsset.value;
      setAssetType(selectedValue);
      formik.setFieldValue('asset', selectedValue);
      dispatch(actions.fetchAssets(selectedValue));
    } else {
      console.error('Selected asset or value is undefined');
    }
  };

  // const assets = [
  //   {
  //     type: 'stock',
  //     items: [
  //       'Reliance Industries',
  //       'Tata Consultancy Services',
  //       'Infosys',
  //       'HDFC Bank',
  //       'ICICI Bank',
  //       'Bharti Airtel',
  //       'Larsen & Toubro',
  //     ],
  //   },
  //   {
  //     type: 'Mutual Fund',
  //     items: [
  //       'Parag Parikh Flexi Cap Fund',
  //       'HDFC Mid-Cap Opportunities Fund',
  //       'HDFC Flexi Cap Fund',
  //       'ICICI Prudential Bluechip Fund',
  //       'Nippon India Small Cap Fund',
  //     ],
  //   },
  // ];

  const Contributor = [{ value: 'Harshit', label: 'Harshit' }];

  const Asset = [
    { value: 'equity', label: 'Equities' },
    { value: 'crypto', label: 'Crypto' },
    { value: 'forex', label: 'Forex' },
  ];

  const dispatch = useDispatch();
  const goals = useSelector(selectGoals);
  const { actions } = useAssetAllocatorSlice();

  useEffect(() => {
    dispatch(actions.fetchGoals());
    dispatch(actions.fetchAssets('equity'));
  }, [dispatch, actions]);

  useEffect(() => {
    if (goals.length > 0 && !selectedGoal) {
      const firstGoal = goals[0];
      setSelectedGoal(firstGoal);
      formik.setFieldValue('goal', firstGoal.id);

      const initialSliderValues: Record<string, number> = {};
      firstGoal.allocations.forEach(allocation => {
        initialSliderValues[allocation.asset.name] = allocation.fixed_value;
      });
      formik.setFieldValue('sliderValues', initialSliderValues);

      const totalAmount = firstGoal.allocations.reduce(
        (sum, allocation) => sum + allocation.fixed_value,
        0,
      );
      setInitial_amount(0);
      setGoalpercentage(calculatePercentageOf(0, totalAmount));
    }
  }, [goals, formik, selectedGoal]);

  const handleGoalChange = (
    option: { value: string; label: string } | null,
  ) => {
    if (!option) return;

    const newSelectedGoal = goals.find(goal => goal.id === option.value);
    if (!newSelectedGoal) return;

    setSelectedGoal(newSelectedGoal);
    formik.setFieldValue('goal', option.value);

    const newSliderValues: Record<string, number> = {};
    newSelectedGoal.allocations.forEach(allocation => {
      newSliderValues[allocation.asset.name] = allocation.fixed_value;
    });
    formik.setFieldValue('sliderValues', newSliderValues);

    const totalAmount = newSelectedGoal.allocations.reduce(
      (sum, allocation) => sum + allocation.fixed_value,
      0,
    );
    setInitial_amount(0);
    setGoalpercentage(calculatePercentageOf(10000, totalAmount));
  };

  const goalOptions = goals.map(goal => ({
    label: goal.name,
    value: goal.id,
  }));

  const fetchedAssets = useSelector(selectAssets);
  console.log(JSON.stringify(fetchedAssets));

  return (
    <Div>
      <div className="px-3 mt-3">
        <div className="px-3">
          <div className="d-flex align-items-center gap-2">
            <img src={prev} alt="Prev" className="cursor-pointer" />
            <BoldText>Asset allocator </BoldText>
            <img src={info} alt="Prev" className="cursor-pointer" />
          </div>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <Boxes
            width="100%"
            height="639px"
            className="d-flex flex-column justify-content-between my-3"
          >
            <div className="h-75">
              <div className="d-flex align-items-center gap-4 p-3 border-bottom">
                <Select
                  name="goal"
                  options={goalOptions}
                  value={
                    selectedGoal
                      ? { label: selectedGoal.name, value: selectedGoal.id }
                      : null
                  }
                  styles={customStyles}
                  onChange={handleGoalChange}
                  defaultValue={Asset.find(option => option.value === 'stock')}
                />
                <Select
                  name="contributor"
                  options={Contributor}
                  styles={customStyles}
                  onChange={option =>
                    formik.setFieldValue('contributor', option?.value)
                  }
                />
                <AllocateText>Advance allocation mode</AllocateText>
                <Switch
                  onChange={handleChange}
                  checked={checked}
                  height={22}
                  width={44}
                  offColor="#E4E6EC"
                  onColor="#E4DFFF"
                  checkedIcon={false}
                  handleDiameter={14}
                  uncheckedIcon={false}
                  offHandleColor="#4C5A81"
                  onHandleColor="#8276F4"
                />
                {checked && (
                  <Select
                    name="asset"
                    onChange={handleAssetChange}
                    options={Asset}
                    styles={customStyles}
                    value={Asset.find(option => option.value === AssetType)}
                    defaultValue={Asset.find(
                      option => option.value === 'equity',
                    )}
                  />
                )}
              </div>
              <div
                className="p-3 overflow-y-auto w-100"
                style={{ height: '100%' }}
              >
                <div className="d-flex w-100">
                  <div className="w-50">
                    <Textsm>Assets</Textsm>
                  </div>
                  <div className="d-flex w-50">
                    <div className="w-50">
                      <Textsm>Allocated Amount</Textsm>
                      <img
                        src={info}
                        alt="Prev"
                        className="cursor-pointer ms-2"
                      />
                    </div>
                    <div className="w-50 text-end">
                      <Textsm>Remaining Amount</Textsm>
                      <img
                        src={info}
                        alt="Prev"
                        className="cursor-pointer ms-2"
                      />
                    </div>
                  </div>
                </div>
                <StyledTable className="table mt-3">
                  <tbody>
                    {!showAssetTable &&
                      selectedGoal &&
                      selectedGoal.allocations.map(allocation => (
                        <StyledTableRow key={allocation.id}>
                          <td>
                            <img src={plus} alt="add-icon" className="" />
                            <Textsmbold
                              color="#22283A"
                              style={{ paddingLeft: '22px' }}
                            >
                              {allocation.asset.name}
                            </Textsmbold>
                          </td>
                          <td>
                            <Textsmbold color="#22283A">
                              ₹{' '}
                              {formik.values.sliderValues[
                                allocation.asset.name
                              ] || 0}
                            </Textsmbold>
                          </td>
                          <td></td>
                          <td>
                            <Textsmbold color="#27ABC0">
                              ₹ {allocation.asset.current_value.toFixed(1)}
                            </Textsmbold>
                          </td>
                        </StyledTableRow>
                      ))}
                    {showAssetTable &&
                      fetchedAssets &&
                      fetchedAssets.map((asset: Asset) => (
                        <StyledTableRow key={asset.id}>
                          <td>
                            <img
                              src={plus}
                              alt="add-icon"
                              className="cursor-pointer"
                            />
                            <Textsmbold
                              color="#22283A"
                              style={{ paddingLeft: '22px' }}
                            >
                              {asset?.currency_group
                                ? asset.symbol
                                : asset.name}
                            </Textsmbold>
                          </td>
                          <td>
                            <Textsmbold color="#22283A">₹ {0}</Textsmbold>
                          </td>
                          <td>
                            <SliderWrapper
                              max={1000}
                              step={1}
                              value={formik.values.sliderValues[asset.id] || 0}
                              onChange={e =>
                                handleSliderChange(
                                  asset.id,
                                  Number(e.target.value),
                                )
                              }
                            />
                          </td>

                          <td>
                            <Textsmbold color="#27ABC0">₹ 0</Textsmbold>
                          </td>
                        </StyledTableRow>
                      ))}
                  </tbody>
                </StyledTable>
              </div>
            </div>
            <div className="d-flex gap-5 mt-3 pb-3 justify-content-between border-top py-3">
              <div className="px-4 d-flex flex-column">
                <Textsmbold color="#22283A">Goal progress</Textsmbold>
                <Textlight>{goalPercentage}% achieved</Textlight>
              </div>
              <div className="d-flex align-items-center px-3 justify-content-center w-50 gap-2">
                <div>
                  <img
                    src={UpArrow}
                    alt="not-found"
                    className="cursor-pointer"
                  />
                </div>
                <div className="w-75">
                  <div className="w-100 d-flex justify-content-between px-3">
                    <div className="d-flex gap-2 align-items-center">
                      <TextRupee>₹ {initial_amount}</TextRupee>
                      <img src={info} alt="info" className="cursor-pointer" />
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <TextRupee>
                        ₹ {formatCurrency(selectedGoal?.target_amount)}
                      </TextRupee>
                      <img src={info} alt="info" className="cursor-pointer" />
                    </div>
                  </div>
                  <ProgressContainer className="w-100">
                    <ProgressBar width={`${goalPercentage}%`}></ProgressBar>
                  </ProgressContainer>
                </div>
                <div>
                  <div
                    className="position-relative"
                    style={{ height: '52px', width: '52px' }}
                  >
                    <StyledCircularProgressbar value={Number(goalPercentage)} />
                    <Textpercentage
                      className="position-absolute"
                      style={{ left: '22%', top: '27%' }}
                    >
                      {goalPercentage}%
                    </Textpercentage>
                  </div>
                </div>
              </div>
              <div className="pe-2">
                <button type="submit" className="capsule-md">
                  Save to Goal
                </button>
              </div>
            </div>
          </Boxes>
        </form>
      </div>
    </Div>
  );
});

const Div = styled.div`
  background-color: #efeef4;
`;
const SliderWrapper = styled.input.attrs({ type: 'range' })`
  width: 90%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 5px;
  background-color: #af8eff;
  border-radius: 3px;
  margin-left: 20px;
  margin-right: 15px;
  margin-top: 7px;

  &:hover {
    opacity: 1;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 15px;
    height: 15px;
    background: #5c54ad;
    cursor: pointer;
    border-radius: 50%;
  }

  &::-moz-range-thumb {
    width: 15px;
    height: 15px;
    background: #5c54ad;
    cursor: pointer;
    border-radius: 50%;
  }
`;
const ProgressContainer = styled.div`
  background-color: #f3f1fe;
  border-radius: 20px;
  height: 15px;
  border: 1px solid #e4e6ec;
`;
interface Progressprop {
  width: string;
}
const ProgressBar = styled.div<Progressprop>`
  background-color: #27abc0;
  border-radius: 20px;
  height: 100%;
  width: ${props => props.width};
  transition: all ease-in 0.3s;
`;
interface Boxprop {
  width: string;
  height: string;
}
const Boxes = styled.div<Boxprop>`
  width: ${props => props.width};
  height: ${props => props.height};
  border: 1px solid #f5f6fa;
  border-radius: 20px;
  background-color: #ffffff;
`;

const BoldText = styled.span`
  font-weight: 800;
  font-size: 20px;
`;
const Textlight = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: #838eae;
`;

const AllocateText = styled.span`
  font-weight: 600;
  font-size: 20px;
`;

interface ColorProp {
  color: string;
}

const Textsmbold = styled.span<ColorProp>`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.color};
`;
const Textsm = styled.span`
  font-weight: 400;
  font-size: 14px;
`;

const TextRupee = styled.span`
  font-weight: 800;
  font-size: 16px;
`;
const Textpercentage = styled.span`
  font-weight: 700;
  font-size: 14px;
`;

const StyledTableRow = styled.tr`
  td {
    padding: 8px, 10px;
    border-bottom: 1px solid #f3f1fe;
    &:nth-child(1) {
      width: 50%;
    } /* First column (icon + name) */
    &:nth-child(2) {
      width: 10%;
      text-align: end;
    } /* Second column (₹0) */
    &:nth-child(3) {
      width: 30%;
    } /* Third column (Slider) */
    &:last-child {
      width: 10%;
    }
  }
`;

const StyledTable = styled.table`
  &.table {
    thead tr th {
      border: none;
      //padding: 16px;
    }

    tbody tr td {
      //padding: 16px;
      border-bottom: 1px solid #f3f1fe;
    }
  }
`;

interface StyledCircularProgressbarProps {
  value: number;
  pathColor?: string;
}
const StyledCircularProgressbar = styled(
  ({ pathColor, ...props }: StyledCircularProgressbarProps) => (
    <CircularProgressbar {...props} />
  ),
).attrs(({ pathColor }) => ({
  styles: buildStyles({
    pathColor: pathColor || '#8276F4', // Default path color
    trailColor: '#EBEFF8',
  }),
}))`
  .CircularProgressbar-path {
    stroke-linecap: round; /* Rounds the path edges */
  }

  .CircularProgressbar-trail {
    stroke-linecap: round; /* Rounds the trail edges */
  }
`;
